import { useEffect, useState } from "react";
import { Plus, Trash2, ChevronUp, ChevronDown, Eye } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { Modal, inputCls, F, SaveBtn } from "./CrmShared";
import { PublicFormRenderer } from "./FormPublic";

const FIELD_TYPES = [
  ["name", "Full Name"], ["text", "Text"], ["phone", "Phone"], ["whatsapp", "WhatsApp"], ["email", "Email"],
  ["number", "Number"], ["date", "Date"], ["datetime", "Date & Time"], ["dropdown", "Dropdown"],
  ["multiselect", "Multi Select"], ["radio", "Radio Buttons"], ["checkbox", "Checkbox"], ["textarea", "Textarea"],
  ["address", "Address"], ["city", "City"], ["state", "State"], ["country", "Country"], ["pincode", "Pincode"],
  ["currency", "Currency"], ["url", "URL"], ["file", "File Upload"],
];

const blankField = (type) => ({ type, label: "", placeholder: "", required: false, help: "", default: "", min_len: 0, max_len: 0, options: [] });

const blankForm = {
  name: "", description: "", fields: [], pipeline_id: "", default_stage: "lead", whatsapp_share_msg: "",
  design: { title: "", description: "", button_text: "Submit", primary_color: "#059669", bg: "#f8fafc", logo: "", layout: "single", success_message: "Thank you! We will get back to you soon." },
};

export default function CrmFormBuilder({ open, onClose, form, pipelines, onSaved }) {
  const [f, setF] = useState(blankForm);
  const [tab, setTab] = useState("fields");
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTab("fields");
    setPreview(false);
    setF(form ? { ...blankForm, ...form, design: { ...blankForm.design, ...(form.design || {}) } } : { ...blankForm, fields: [blankField("name"), { ...blankField("phone"), label: "Phone" }].map((x) => ({ ...x, label: x.label || (x.type === "name" ? "Full Name" : "Phone") })) });
  }, [open, form]);

  const set = (k, v) => setF({ ...f, [k]: v });
  const setDesign = (k, v) => setF({ ...f, design: { ...f.design, [k]: v } });
  const updField = (i, k, v) => setF({ ...f, fields: f.fields.map((x, j) => (j === i ? { ...x, [k]: v } : x)) });
  const addField = (type) => {
    const label = FIELD_TYPES.find(([t]) => t === type)?.[1] || "Field";
    setF({ ...f, fields: [...f.fields, { ...blankField(type), label }] });
  };
  const mv = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= f.fields.length) return;
    const a = [...f.fields];
    [a[i], a[j]] = [a[j], a[i]];
    setF({ ...f, fields: a });
  };
  const delField = (i) => setF({ ...f, fields: f.fields.filter((_, j) => j !== i) });

  const stageOptions = (pipelines.find((p) => p.id === f.pipeline_id) || pipelines[0])?.stages || [];

  const save = async (publish = false) => {
    setSaving(true);
    try {
      let saved;
      if (form?.id) saved = (await api.put(`/store-admin/crm/forms/${form.id}`, f)).data;
      else saved = (await api.post("/store-admin/crm/forms", f)).data;
      if (publish && saved.status !== "published") await api.post(`/store-admin/crm/forms/${saved.id}/status`, { status: "published" });
      toast.success(publish ? "Form published" : "Form saved");
      onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };

  const tabBtn = (k, l) => (
    <button key={k} onClick={() => setTab(k)} data-testid={`fb-tab-${k}`}
      className={`h-9 px-4 rounded-full text-xs font-extrabold border ${tab === k ? "bg-foreground text-background border-foreground" : "border-border hover:bg-muted"}`}>{l}</button>
  );

  return (
    <Modal open={open} onClose={onClose} title={form ? `Edit Form — ${form.name}` : "Create New Form"} testid="form-builder">
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {tabBtn("fields", "Fields")}{tabBtn("design", "Design")}{tabBtn("routing", "Pipeline & Share")}
          <button onClick={() => setPreview(!preview)} data-testid="fb-preview-toggle"
            className={`h-9 px-4 rounded-full text-xs font-extrabold border flex items-center gap-1.5 ${preview ? "bg-emerald-600 text-white border-emerald-600" : "border-border"}`}>
            <Eye className="h-3.5 w-3.5" /> Preview
          </button>
        </div>

        <F label="Form name *"><input data-testid="fb-name" className={inputCls} value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Real Estate Enquiry Form" /></F>

        {preview ? (
          <div className="rounded-2xl border border-border overflow-hidden" data-testid="fb-live-preview">
            <PublicFormRenderer form={{ ...f, fields: f.fields.map((x, i) => ({ ...x, key: x.label.toLowerCase().replace(/[^a-z0-9]+/g, "_") || `field_${i}`, order: i })) }} preview />
          </div>
        ) : tab === "fields" ? (
          <div className="space-y-2" data-testid="fb-fields">
            {f.fields.map((fld, i) => (
              <div key={i} className="rounded-xl border border-border overflow-hidden" data-testid={`fb-field-${i}`}>
                <div className="flex items-center gap-1.5 px-2.5 py-2 bg-muted/40">
                  <span className="text-xs font-bold flex-1 truncate">{i + 1}. {fld.label || "Untitled"} <span className="text-muted-foreground font-medium">({FIELD_TYPES.find(([t]) => t === fld.type)?.[1]})</span>{fld.required && <span className="text-red-500"> *</span>}</span>
                  <button onClick={() => mv(i, -1)} className="h-7 w-7 rounded-md hover:bg-muted flex items-center justify-center"><ChevronUp className="h-3.5 w-3.5" /></button>
                  <button onClick={() => mv(i, 1)} className="h-7 w-7 rounded-md hover:bg-muted flex items-center justify-center"><ChevronDown className="h-3.5 w-3.5" /></button>
                  <button onClick={() => delField(i)} className="h-7 w-7 rounded-md hover:bg-red-50 hover:text-red-600 flex items-center justify-center" data-testid={`fb-field-del-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
                <div className="p-3 grid sm:grid-cols-2 gap-2.5">
                  <F label="Label"><input className={inputCls} data-testid={`fb-field-label-${i}`} value={fld.label} onChange={(e) => updField(i, "label", e.target.value)} /></F>
                  <F label="Placeholder"><input className={inputCls} value={fld.placeholder} onChange={(e) => updField(i, "placeholder", e.target.value)} /></F>
                  {["dropdown", "multiselect", "radio"].includes(fld.type) && (
                    <div className="sm:col-span-2"><F label="Options (one per line)">
                      <textarea rows={2} className={`${inputCls} h-auto py-2`} data-testid={`fb-field-options-${i}`} value={(fld.options || []).join("\n")} onChange={(e) => updField(i, "options", e.target.value.split("\n"))} />
                    </F></div>
                  )}
                  <F label="Help text"><input className={inputCls} value={fld.help} onChange={(e) => updField(i, "help", e.target.value)} /></F>
                  <F label="Default value"><input className={inputCls} value={fld.default} onChange={(e) => updField(i, "default", e.target.value)} /></F>
                  <F label="Min length"><input type="number" min="0" className={inputCls} value={fld.min_len} onChange={(e) => updField(i, "min_len", e.target.value)} /></F>
                  <F label="Max length"><input type="number" min="0" className={inputCls} value={fld.max_len} onChange={(e) => updField(i, "max_len", e.target.value)} /></F>
                  <label className="flex items-center gap-2 text-xs font-bold cursor-pointer sm:col-span-2">
                    <input type="checkbox" data-testid={`fb-field-required-${i}`} checked={fld.required} onChange={(e) => updField(i, "required", e.target.checked)} className="h-4 w-4 accent-emerald-600" /> Required field
                  </label>
                </div>
              </div>
            ))}
            <div>
              <p className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground mb-1.5">Add field</p>
              <div className="flex flex-wrap gap-1.5" data-testid="fb-add-field">
                {FIELD_TYPES.map(([t, l]) => (
                  <button key={t} onClick={() => addField(t)} data-testid={`fb-add-${t}`}
                    className="h-8 px-3 rounded-lg border border-dashed border-border text-[11px] font-bold hover:border-emerald-500 hover:text-emerald-700 transition-colors flex items-center gap-1">
                    <Plus className="h-3 w-3" /> {l}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : tab === "design" ? (
          <div className="grid sm:grid-cols-2 gap-3" data-testid="fb-design">
            <F label="Form title (public)"><input className={inputCls} data-testid="fb-design-title" value={f.design.title} onChange={(e) => setDesign("title", e.target.value)} placeholder={f.name || "Form title"} /></F>
            <F label="Button text"><input className={inputCls} value={f.design.button_text} onChange={(e) => setDesign("button_text", e.target.value)} /></F>
            <div className="sm:col-span-2"><F label="Description"><textarea rows={2} className={`${inputCls} h-auto py-2`} value={f.design.description} onChange={(e) => setDesign("description", e.target.value)} /></F></div>
            <F label="Primary color"><input type="color" className="h-10 w-full rounded-xl border border-input cursor-pointer" data-testid="fb-design-color" value={f.design.primary_color} onChange={(e) => setDesign("primary_color", e.target.value)} /></F>
            <F label="Background"><input type="color" className="h-10 w-full rounded-xl border border-input cursor-pointer" value={f.design.bg} onChange={(e) => setDesign("bg", e.target.value)} /></F>
            <F label="Logo URL (optional)"><input className={inputCls} value={f.design.logo} onChange={(e) => setDesign("logo", e.target.value)} /></F>
            <F label="Layout">
              <select className={inputCls} value={f.design.layout} onChange={(e) => setDesign("layout", e.target.value)}>
                <option value="single">Single column</option><option value="card">Centered card</option>
              </select>
            </F>
            <div className="sm:col-span-2"><F label="Success message"><input className={inputCls} value={f.design.success_message} onChange={(e) => setDesign("success_message", e.target.value)} /></F></div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="fb-routing">
            <F label="Default pipeline">
              <select className={inputCls} data-testid="fb-pipeline" value={f.pipeline_id} onChange={(e) => set("pipeline_id", e.target.value)}>
                <option value="">Sales Pipeline (default)</option>
                {pipelines.filter((p) => !p.is_default).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                {pipelines.filter((p) => p.is_default).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </F>
            <F label="Default stage for new leads">
              <select className={inputCls} data-testid="fb-stage" value={f.default_stage} onChange={(e) => set("default_stage", e.target.value)}>
                {(stageOptions.length ? stageOptions : [{ key: "lead", name: "Lead" }]).map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
              </select>
            </F>
            <F label="WhatsApp share message (customizable)">
              <textarea rows={2} className={`${inputCls} h-auto py-2`} data-testid="fb-wa-msg" value={f.whatsapp_share_msg} onChange={(e) => set("whatsapp_share_msg", e.target.value)} placeholder="Hello! Please fill out this form to share your details with us:" />
            </F>
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <button onClick={() => save(false)} disabled={saving} data-testid="fb-save"
            className="flex-1 h-11 rounded-xl border border-border font-bold text-sm hover:bg-muted disabled:opacity-60">
            {saving ? "Saving…" : "Save Draft"}
          </button>
          <button onClick={() => save(true)} disabled={saving} data-testid="fb-publish"
            className="flex-1 h-11 rounded-xl font-bold text-sm text-white disabled:opacity-60"
            style={{ backgroundImage: "linear-gradient(135deg, #059669, #0d9488)" }}>
            {saving ? "…" : form?.status === "published" ? "Update (Live)" : "Publish Form"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
